import { z } from 'zod';
import type { PaginatedResponse } from './api-envelope.js';
import type { BenchmarkRunStatus, GroupedCaseResult } from './benchmarks.js';
import type { SkillPackageContextCoverage } from './skill-packages.js';
import { projectBenchmarkModelResolutionEvidence } from './benchmark-model-config.js';
import type {
  BenchmarkModelResolutionEvidence,
  BenchmarkModelProvenanceStatus,
} from './benchmark-model-config.js';

export type BenchmarkComparisonMode = 'NO_SKILL' | 'PREVIOUS_PACKAGE';
export type BenchmarkComparisonRole = 'CONTROL' | 'VARIANT';
export type BenchmarkComparisonValidityReason =
  | 'PARTIAL_SOURCE_COVERAGE'
  | 'UNKNOWN_SOURCE_PROVENANCE'
  | 'UNKNOWN_SOURCE_COVERAGE'
  | 'DIFFERENT_FIXTURE'
  | 'DIFFERENT_CONFIGURATION'
  | 'UNKNOWN_CONFIGURATION'
  | 'UNKNOWN_MODEL_PROVENANCE'
  | 'MODEL_MISMATCH'
  | 'INCOMPLETE_RESULTS'
  | 'INCOMPLETE_USAGE'
  | 'INVALID_PAIRING'
  | 'UNSUPPORTED_METHOD';
export interface StartBenchmarkComparisonRequest {
  benchmarkId: number;
  mode: BenchmarkComparisonMode;
  controlPackageVersionId?: number;
  variantPackageVersionId?: number;
  idempotencyKey: string;
  retryOfComparisonId?: number;
  acknowledgeUnknownPriorUsage?: boolean;
}
const id = z.number().int().min(1).max(2147483647);
export const StartBenchmarkComparisonSchema = z
  .object({
    benchmarkId: id,
    mode: z.enum(['NO_SKILL', 'PREVIOUS_PACKAGE']),
    controlPackageVersionId: id.optional(),
    variantPackageVersionId: id.optional(),
    idempotencyKey: z.string().regex(/^[A-Za-z0-9._:-]{1,128}$/),
    retryOfComparisonId: id.optional(),
    acknowledgeUnknownPriorUsage: z.boolean().optional(),
  })
  .strict()
  .superRefine((input, ctx) => {
    if (input.mode === 'NO_SKILL' && input.controlPackageVersionId !== undefined)
      ctx.addIssue({
        code: 'custom',
        path: ['controlPackageVersionId'],
        message: 'No skill has no package selector.',
      });
    if (
      input.mode === 'PREVIOUS_PACKAGE' &&
      input.controlPackageVersionId === undefined &&
      input.retryOfComparisonId === undefined
    )
      ctx.addIssue({
        code: 'custom',
        path: ['controlPackageVersionId'],
        message: 'Select a previous package.',
      });
  });
export interface BenchmarkComparisonArmSummary {
  role: BenchmarkComparisonRole;
  runId: number;
  status: BenchmarkRunStatus;
  sourceMode: 'NO_SKILL' | 'PACKAGE' | 'LEGACY_ROOT';
  skillId: number;
  packageVersionId: number | null;
  packageDigest: string | null;
  coverage: SkillPackageContextCoverage | null;
  captureVersion: string | null;
  overallScore: number | null;
  totalScore: number | null;
  maxPossibleScore: number | null;
  caseCount: number | null;
  repetitionsPerCase: number | null;
  expectedRepetitions: number | null;
  completedRepetitions: number | null;
  failedRepetitions: number | null;
  interactionsConsumed: number | null;
  usageIncomplete: boolean | null;
  evaluationMethodVersion: string | null;
  evaluationConfigDigest: string | null;
  modelResolutionEvidence: BenchmarkModelResolutionEvidence | null;
  modelProvenanceStatus: BenchmarkModelProvenanceStatus;
  executionProvider: string | null;
  executionModel: string | null;
  scorerProvider: string | null;
  scorerModel: string | null;
}
export interface BenchmarkComparisonResponse {
  id: number;
  workspaceId: number;
  benchmarkId: number;
  skillId: number;
  mode: BenchmarkComparisonMode;
  status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'INCOMPLETE';
  retryOfComparisonId: number | null;
  fixtureDigest: string | null;
  evaluationConfigDigest: string | null;
  evaluationMethodVersion: string | null;
  sourceScope: 'FULL_PACKAGE' | 'LIMITED_ROOT_ONLY' | 'INCOMPLETE_PACKAGE' | 'UNKNOWN';
  validity: 'COMPARABLE' | 'INCOMPLETE' | 'INCOMPATIBLE' | 'UNKNOWN';
  reasons: BenchmarkComparisonValidityReason[];
  control: BenchmarkComparisonArmSummary;
  variant: BenchmarkComparisonArmSummary;
  caseCount: number;
  repetitionsPerCase: number;
  deltaPercentagePoints: number | null;
  differenceInterval: { low: number; high: number } | null;
  uncertaintyStatus: 'AVAILABLE' | 'INSUFFICIENT_CASES' | 'DEGENERATE' | 'UNAVAILABLE';
  method: string | null;
  confidenceLevel: number | null;
  replicateCount: number | null;
  seed: number | null;
  rngVersion: string | null;
  quantileVersion: string | null;
  executionWrapperVersion: string | null;
  createdAt: string;
  completedAt: string | null;
}
export interface BenchmarkComparisonSourcesResponse {
  skillId: number;
  currentPackageVersionId: number | null;
  legacyRootAvailable: boolean;
  packageVersions: Array<{ id: number; digest: string; createdAt: string; isCurrent: boolean }>;
}
export type BenchmarkComparisonListResponse = PaginatedResponse<
  BenchmarkComparisonResponse,
  number
>;
/** Authorized raw output belongs only to this dedicated endpoint/cache. */
export interface BenchmarkComparisonArmDetailResponse {
  comparisonId: number;
  workspaceId: number;
  benchmarkId: number;
  skillId: number;
  role: BenchmarkComparisonRole;
  runId: number;
  arm: BenchmarkComparisonArmSummary;
  fixtureDigest: string | null;
  startedAt: string | null;
  completedAt: string | null;
  errorMessage: string | null;
  caseResults: GroupedCaseResult[];
}
// Read own data properties only: never invoke getters or spread captured/persisted objects.
function own(input: unknown, key: string): unknown {
  return input !== null && typeof input === 'object'
    ? Object.getOwnPropertyDescriptor(input, key)?.value
    : undefined;
}
function requiredId(value: unknown): number {
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 1 || value > 2147483647)
    throw new Error('Invalid comparison identity.');
  return value;
}
function numberOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}
function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}
function enumeration<T extends string>(value: unknown, choices: readonly T[]): T {
  if (typeof value !== 'string' || !choices.includes(value as T))
    throw new Error('Invalid comparison state.');
  return value as T;
}
function coverage(input: unknown): SkillPackageContextCoverage | null {
  if (input == null) return null;
  const files = own(input, 'files'),
    missing = own(input, 'missingReferences');
  if (
    !Array.isArray(files) ||
    !Array.isArray(missing) ||
    typeof own(input, 'complete') !== 'boolean' ||
    typeof own(input, 'digest') !== 'string'
  )
    return null;
  return {
    digest: own(input, 'digest') as string,
    complete: own(input, 'complete') as boolean,
    files: files.map((file) => ({
      path: stringOrNull(own(file, 'path')) ?? '',
      sha256: stringOrNull(own(file, 'sha256')) ?? '',
      status: enumeration(own(file, 'status'), ['included', 'unsupported', 'omitted']),
      ...(typeof own(file, 'reason') === 'string' ? { reason: own(file, 'reason') as string } : {}),
    })),
    missingReferences: missing.map((ref) => ({
      source: stringOrNull(own(ref, 'source')) ?? '',
      path: stringOrNull(own(ref, 'path')) ?? '',
      ...(typeof own(ref, 'reason') === 'string' ? { reason: own(ref, 'reason') as string } : {}),
    })),
  };
}
/** Public event projection is fail closed on malformed historical coverage. */
export function projectBenchmarkContextCoverage(
  input: unknown
): SkillPackageContextCoverage | null {
  try {
    return coverage(input);
  } catch {
    return null;
  }
}
export function createBenchmarkComparisonArmSummary(
  input: BenchmarkComparisonArmSummary
): BenchmarkComparisonArmSummary {
  return {
    role: enumeration(own(input, 'role'), ['CONTROL', 'VARIANT']),
    runId: requiredId(own(input, 'runId')),
    status: enumeration(own(input, 'status'), [
      'PENDING',
      'RUNNING',
      'COMPLETED',
      'FAILED',
      'CANCELLED',
    ]),
    sourceMode: enumeration(own(input, 'sourceMode'), ['NO_SKILL', 'PACKAGE', 'LEGACY_ROOT']),
    skillId: requiredId(own(input, 'skillId')),
    packageVersionId: numberOrNull(own(input, 'packageVersionId')),
    packageDigest: stringOrNull(own(input, 'packageDigest')),
    coverage: coverage(own(input, 'coverage')),
    captureVersion: stringOrNull(own(input, 'captureVersion')),
    overallScore: numberOrNull(own(input, 'overallScore')),
    totalScore: numberOrNull(own(input, 'totalScore')),
    maxPossibleScore: numberOrNull(own(input, 'maxPossibleScore')),
    caseCount: numberOrNull(own(input, 'caseCount')),
    repetitionsPerCase: numberOrNull(own(input, 'repetitionsPerCase')),
    expectedRepetitions: numberOrNull(own(input, 'expectedRepetitions')),
    completedRepetitions: numberOrNull(own(input, 'completedRepetitions')),
    failedRepetitions: numberOrNull(own(input, 'failedRepetitions')),
    interactionsConsumed: numberOrNull(own(input, 'interactionsConsumed')),
    usageIncomplete:
      typeof own(input, 'usageIncomplete') === 'boolean'
        ? (own(input, 'usageIncomplete') as boolean)
        : null,
    evaluationMethodVersion: stringOrNull(own(input, 'evaluationMethodVersion')),
    evaluationConfigDigest: stringOrNull(own(input, 'evaluationConfigDigest')),
    modelResolutionEvidence: projectBenchmarkModelResolutionEvidence(
      own(input, 'modelResolutionEvidence')
    ),
    modelProvenanceStatus: enumeration(own(input, 'modelProvenanceStatus'), [
      'MATCHED',
      'UNKNOWN',
      'MISMATCH',
      'MIXED',
    ]),
    executionProvider: stringOrNull(own(input, 'executionProvider')),
    executionModel: stringOrNull(own(input, 'executionModel')),
    scorerProvider: stringOrNull(own(input, 'scorerProvider')),
    scorerModel: stringOrNull(own(input, 'scorerModel')),
  };
}
const reasons: readonly BenchmarkComparisonValidityReason[] = [
  'PARTIAL_SOURCE_COVERAGE',
  'UNKNOWN_SOURCE_PROVENANCE',
  'UNKNOWN_SOURCE_COVERAGE',
  'DIFFERENT_FIXTURE',
  'DIFFERENT_CONFIGURATION',
  'UNKNOWN_CONFIGURATION',
  'UNKNOWN_MODEL_PROVENANCE',
  'MODEL_MISMATCH',
  'INCOMPLETE_RESULTS',
  'INCOMPLETE_USAGE',
  'INVALID_PAIRING',
  'UNSUPPORTED_METHOD',
];
/** Server-owned normalized wire input; projection never grants source access. */
export function createBenchmarkComparisonResponse(
  input: BenchmarkComparisonResponse
): BenchmarkComparisonResponse {
  const interval = own(input, 'differenceInterval');
  const low = numberOrNull(own(interval, 'low')),
    high = numberOrNull(own(interval, 'high'));
  const rawReasons = own(input, 'reasons');
  return {
    id: requiredId(own(input, 'id')),
    workspaceId: requiredId(own(input, 'workspaceId')),
    benchmarkId: requiredId(own(input, 'benchmarkId')),
    skillId: requiredId(own(input, 'skillId')),
    mode: enumeration(own(input, 'mode'), ['NO_SKILL', 'PREVIOUS_PACKAGE']),
    status: enumeration(own(input, 'status'), ['PENDING', 'RUNNING', 'COMPLETED', 'INCOMPLETE']),
    retryOfComparisonId: numberOrNull(own(input, 'retryOfComparisonId')),
    fixtureDigest: stringOrNull(own(input, 'fixtureDigest')),
    evaluationConfigDigest: stringOrNull(own(input, 'evaluationConfigDigest')),
    evaluationMethodVersion: stringOrNull(own(input, 'evaluationMethodVersion')),
    sourceScope: enumeration(own(input, 'sourceScope'), [
      'FULL_PACKAGE',
      'LIMITED_ROOT_ONLY',
      'INCOMPLETE_PACKAGE',
      'UNKNOWN',
    ]),
    validity: enumeration(own(input, 'validity'), [
      'COMPARABLE',
      'INCOMPLETE',
      'INCOMPATIBLE',
      'UNKNOWN',
    ]),
    reasons: Array.isArray(rawReasons)
      ? rawReasons.filter((reason): reason is BenchmarkComparisonValidityReason =>
          reasons.includes(reason)
        )
      : [],
    control: createBenchmarkComparisonArmSummary(
      own(input, 'control') as BenchmarkComparisonArmSummary
    ),
    variant: createBenchmarkComparisonArmSummary(
      own(input, 'variant') as BenchmarkComparisonArmSummary
    ),
    caseCount: requiredId(own(input, 'caseCount')),
    repetitionsPerCase: requiredId(own(input, 'repetitionsPerCase')),
    deltaPercentagePoints: numberOrNull(own(input, 'deltaPercentagePoints')),
    differenceInterval: low !== null && high !== null && low <= high ? { low, high } : null,
    uncertaintyStatus: enumeration(own(input, 'uncertaintyStatus'), [
      'AVAILABLE',
      'INSUFFICIENT_CASES',
      'DEGENERATE',
      'UNAVAILABLE',
    ]),
    method: stringOrNull(own(input, 'method')),
    confidenceLevel: numberOrNull(own(input, 'confidenceLevel')),
    replicateCount: numberOrNull(own(input, 'replicateCount')),
    seed: numberOrNull(own(input, 'seed')),
    rngVersion: stringOrNull(own(input, 'rngVersion')),
    quantileVersion: stringOrNull(own(input, 'quantileVersion')),
    executionWrapperVersion: stringOrNull(own(input, 'executionWrapperVersion')),
    createdAt: stringOrNull(own(input, 'createdAt')) ?? '',
    completedAt: stringOrNull(own(input, 'completedAt')),
  };
}
export function createBenchmarkComparisonSourcesResponse(
  input: BenchmarkComparisonSourcesResponse
): BenchmarkComparisonSourcesResponse {
  const versions = own(input, 'packageVersions');
  return {
    skillId: requiredId(own(input, 'skillId')),
    currentPackageVersionId: numberOrNull(own(input, 'currentPackageVersionId')),
    legacyRootAvailable: own(input, 'legacyRootAvailable') === true,
    packageVersions: Array.isArray(versions)
      ? versions.map((item) => ({
          id: requiredId(own(item, 'id')),
          digest: stringOrNull(own(item, 'digest')) ?? '',
          createdAt: stringOrNull(own(item, 'createdAt')) ?? '',
          isCurrent: own(item, 'isCurrent') === true,
        }))
      : [],
  };
}
export function createBenchmarkComparisonListResponse(
  input: BenchmarkComparisonListResponse
): BenchmarkComparisonListResponse {
  const data = own(input, 'data');
  return {
    data: Array.isArray(data) ? data.map(createBenchmarkComparisonResponse) : [],
    meta: {
      nextCursor: numberOrNull(own(own(input, 'meta'), 'nextCursor')),
      hasNextPage: own(own(input, 'meta'), 'hasNextPage') === true,
      totalCount: numberOrNull(own(own(input, 'meta'), 'totalCount')) ?? 0,
    },
  };
}
export function createBenchmarkComparisonArmDetailResponse(
  input: BenchmarkComparisonArmDetailResponse
): BenchmarkComparisonArmDetailResponse {
  const raw = own(input, 'caseResults');
  return {
    comparisonId: requiredId(own(input, 'comparisonId')),
    workspaceId: requiredId(own(input, 'workspaceId')),
    benchmarkId: requiredId(own(input, 'benchmarkId')),
    skillId: requiredId(own(input, 'skillId')),
    role: enumeration(own(input, 'role'), ['CONTROL', 'VARIANT']),
    runId: requiredId(own(input, 'runId')),
    arm: createBenchmarkComparisonArmSummary(own(input, 'arm') as BenchmarkComparisonArmSummary),
    fixtureDigest: stringOrNull(own(input, 'fixtureDigest')),
    startedAt: stringOrNull(own(input, 'startedAt')),
    completedAt: stringOrNull(own(input, 'completedAt')),
    errorMessage: stringOrNull(own(input, 'errorMessage')),
    caseResults: Array.isArray(raw)
      ? raw.map((item) => ({
          caseId: requiredId(own(item, 'caseId')),
          caseOrdinal: requiredId(own(item, 'caseOrdinal')),
          expectedRepetitions: numberOrNull(own(item, 'expectedRepetitions')),
          caseSnapshot: {
            inputPrompt: stringOrNull(own(own(item, 'caseSnapshot'), 'inputPrompt')) ?? '',
            expectedOutput: stringOrNull(own(own(item, 'caseSnapshot'), 'expectedOutput')) ?? '',
            maxScore: numberOrNull(own(own(item, 'caseSnapshot'), 'maxScore')) ?? 0,
          },
          caseMeanPoints: numberOrNull(own(item, 'caseMeanPoints')) ?? 0,
          casePercentage: numberOrNull(own(item, 'casePercentage')) ?? 0,
          repetitions: Array.isArray(own(item, 'repetitions'))
            ? (own(item, 'repetitions') as unknown[]).map((rep) => ({
                repetition: requiredId(own(rep, 'repetition')),
                rawOutput: stringOrNull(own(rep, 'rawOutput')) ?? '',
                score: numberOrNull(own(rep, 'score')) ?? 0,
                scorerReasoning: stringOrNull(own(rep, 'scorerReasoning')) ?? '',
                executionInteractionConsumed: own(rep, 'executionInteractionConsumed') === true,
                scoringInteractionConsumed: own(rep, 'scoringInteractionConsumed') === true,
                outcome:
                  own(rep, 'outcome') === 'SUCCEEDED' || own(rep, 'outcome') === 'FAILED'
                    ? (own(rep, 'outcome') as 'SUCCEEDED' | 'FAILED')
                    : null,
                attempts: numberOrNull(own(rep, 'attempts')),
                executionInteractionsConsumed: numberOrNull(
                  own(rep, 'executionInteractionsConsumed')
                ),
                scoringInteractionsConsumed: numberOrNull(own(rep, 'scoringInteractionsConsumed')),
              }))
            : [],
        }))
      : [],
  };
}
